import { GOOGLE_MAPS_API_KEY } from 'utils/googlePlaceUtils';

/**
 * Loads the Google Maps JavaScript API once per page.
 *
 * The address search used to get this script as a side effect of the
 * react-google-autocomplete widget. Now that predictions are requested
 * directly, the script is loaded here instead, and every caller shares a single
 * in-flight promise so that mounting several search inputs -- the app header
 * plus a page's own search box, which happens on the landing page -- still
 * results in exactly one script tag.
 *
 * Only the places library is requested. No map is ever constructed anywhere in
 * the app (all maps are Leaflet), and loading the library alone is not billed;
 * only the prediction requests are.
 */

const SCRIPT_ID = 'google-maps-places-js';

let loadPromise = null;

/**
 * Returns true once google.maps.places is available on the page.
 *
 * @returns {boolean} Whether the places library is ready to use
 */
function placesReady() {
	return Boolean(window.google && window.google.maps && window.google.maps.places);
}

/**
 * Loads the Maps JS places library, resolving when it is ready to use.
 *
 * Resolves immediately when the library is already present, so a remount never
 * re-fetches it. A failure rejects and clears the memoized promise, so a later
 * attempt can retry rather than being stuck with a permanently failed load.
 *
 * @returns {Promise<void>} Resolves when google.maps.places is usable
 */
export function loadGooglePlaces() {
	if (placesReady()) {
		return Promise.resolve();
	}

	if (loadPromise) {
		return loadPromise;
	}

	loadPromise = new Promise((resolve, reject) => {
		const existing = document.getElementById(SCRIPT_ID);

		const onReady = () => (placesReady() ? resolve() : reject(new Error('Google places library did not initialize')));

		if (existing) {
			existing.addEventListener('load', onReady);
			existing.addEventListener('error', reject);
			return;
		}

		const script = document.createElement('script');
		script.id = SCRIPT_ID;
		script.async = true;
		script.defer = true;
		script.src =
			'https://maps.googleapis.com/maps/api/js' +
			`?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}` +
			'&libraries=places' +
			'&language=en';
		script.addEventListener('load', onReady);
		script.addEventListener('error', reject);

		document.head.appendChild(script);
	}).catch((error) => {
		// Clear the memo so a later mount can retry a failed load.
		loadPromise = null;
		throw error;
	});

	return loadPromise;
}
